import { STAFF_STATE } from '@fitness-force/state';
import { BehaviorSubject, Subject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormGroup,
  UntypedFormBuilder,
  UntypedFormControl,
} from '@angular/forms';
import { MultiSelectModule } from 'primeng/multiselect';
import { StyleClassModule } from 'primeng/styleclass';
import type { STAFFINTERFACE, STATE_STAFF_LIST } from '@fitness-force/models';
import { TranslateModule } from '@ngx-translate/core';
import { AuthSevices } from '@fitness-force/api';
import { SelectItemGroup } from 'primeng/api';

const STAFF_OPTION_ITEM_SIZE = 38;

@Component({
  selector: 'multi-select-staff',
  templateUrl: './multi-select-staff.component.html',
  styleUrls: ['./multi-select-staff.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    MultiSelectModule,
    FormsModule,
    ReactiveFormsModule,
    StyleClassModule,
    TranslateModule,
  ],
})
export class MultiSelectStaffComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly fb = inject(UntypedFormBuilder);
  private readonly authService = inject(AuthSevices);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  readonly selected = input<number[]>([]);
  readonly placeholder = input<string>('ADMIN.COMMON.SELECT_STAFF');
  readonly excludeSystemStaff = input<boolean>(false);
  selectedFilter: { name: string } | undefined;
  allStaffs$ = new BehaviorSubject<SelectItemGroup[]>([]);
  selectedStaffList: UntypedFormGroup = new UntypedFormGroup({});
  readonly selectStaffEvent = output();
  readonly loading = signal(true);
  readonly virtualScrollItemSize = STAFF_OPTION_ITEM_SIZE;

  statusOptions: any[] = [
    { label: 'Invited', value: 'Invited' },
    { label: 'Active', value: 'Active' },
    { label: 'Suspended', value: 'Suspended' },
    { label: 'Terminated', value: 'Terminated' },
  ];

  nestedArray: SelectItemGroup[] = [];

  ngOnInit(): void {
    this.selectedStaffList = this.fb.group({
      selectedStaffs: [],
    });
    this.loadStaffList();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadStaffList() {
    this.store
      .select(STAFF_STATE)
      .pipe(
        map((s: STATE_STAFF_LIST) => s?.staffList ?? []),
        distinctUntilChanged(),
        takeUntil(this.destroy$),
      )
      .subscribe((staffList: STAFFINTERFACE[]) => {
        let all = staffList;
        if (this.excludeSystemStaff()) {
          all = all.filter(
            (val: STAFFINTERFACE) =>
              !val?.is_system_staff && !val?.is_for_background_job,
          );
        }
        this.nestedArray = [];

        if (all && all.length > 0) {
          if (this.authService.currentUser == 'Admin') {
            this.nestedArray = this.buildStatusGroups(all);
            this.applySingleStaffDisableLogic();
            this.allStaffs$.next(this.nestedArray);
          } else {
            let temp: STAFFINTERFACE[] = [];
            const currentUserDetails = this.authService.decodedToken;
            if (
              currentUserDetails &&
              (currentUserDetails['can_admin_client_profiles'] == 'true ' ||
                currentUserDetails['can_manage_client_profiles'] == 'true')
            ) {
              temp = all;
            } else {
              const currentUserId = currentUserDetails?.Id;
              for (const staff of all) {
                if (
                  staff.reports_to_id != null &&
                  staff.reports_to_id.toString() == currentUserId
                ) {
                  temp.push(staff);
                }
              }
              if (
                !temp.find(
                  (staff: STAFFINTERFACE) =>
                    staff.id.toString() == currentUserId,
                )
              ) {
                const currentUser = all.find(
                  (staff: STAFFINTERFACE) =>
                    staff.id.toString() == currentUserId,
                );
                if (currentUser) temp.push(currentUser);
              }
            }

            this.nestedArray = this.buildStatusGroups(temp);
            this.applySingleStaffDisableLogic();
            this.allStaffs$.next(temp as any);
          }
        }

        const selected = this.selected();
        if (all && all.length > 0 && selected && selected.length > 0) {
          const selectedIds = new Set(selected);
          const staffs: STAFFINTERFACE[] = [];
          for (const staff of all) {
            if (selectedIds.has(staff.id)) {
              staffs.push(staff);
            }
          }
          this.selectedStaffList.get('selectedStaffs')?.setValue(staffs);
        }

        this.loading.set(false);
        this.cdr.markForCheck();
      });
  }

  /** Builds the same status-grouped SelectItemGroup[] as before, in one pass per status. */
  private buildStatusGroups(staffList: STAFFINTERFACE[]): SelectItemGroup[] {
    const groups: SelectItemGroup[] = [];
    for (const status of this.statusOptions) {
      const staffs = staffList.filter((item) => item.status === status.value);
      if (staffs.length > 0) {
        const group: SelectItemGroup = {} as SelectItemGroup;
        group.label = status.label;
        group.items = staffs.map((o) => ({
          label: o.name,
          value: o,
          disabled: false,
        }));
        groups.push(group);
      }
    }
    return groups;
  }

  /** Existing single-staff disable behavior (unchanged). */
  private applySingleStaffDisableLogic(): void {
    if (this.nestedArray.length == 1) {
      this.nestedArray.forEach((element) => {
        if (element.items.length > 0) {
          if (element.items.length == 1) {
            element.items.forEach((staff) => {
              staff.disabled = true;
            });
            this.selectedStaffList.get('selectedStaffs')?.disable();
          }
        }
      });
    }
  }

  // Run this on exchange
  setSelectedStaff(allList: STAFFINTERFACE[]) {
    const selectedIds = new Set(this.selected());
    const tempSelected: STAFFINTERFACE[] = [];
    for (const elm of allList) {
      if (selectedIds.has(elm.id)) {
        tempSelected.push(elm);
      }
    }
    this.selectedStaffs.setValue(tempSelected);
  }

  get selectedStaffs(): UntypedFormControl {
    return this.selectedStaffList.get('selectedStaffs') as UntypedFormControl;
  }

  getSelectedStaff(e: any) {
    this.selectStaffEvent.emit(this.selectedStaffs.value);
  }

  selectAll() {
    this.selectedStaffs.setValue(this.allStaffs$.value);
    this.selectStaffEvent.emit(this.selectedStaffs.value);
  }
}
